import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingBag, ChevronRight, Minus, Plus, CheckCircle2, Truck, Shield, RotateCcw, Dumbbell, ArrowLeft } from 'lucide-react';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  const product = products.find(p => p.slug === slug);
  const category = product ? categories.find(c => c.id === product.categoryId) : null;
  const relatedProducts = product
    ? products.filter(p => p.categoryId !== product.categoryId && p.id !== product.id).slice(0, 4)
    : [];

  useEffect(() => {
    if (product) document.title = `${product.name} | Sport Fitt Pro`;
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <CartDrawer />
        <main className="pt-[104px]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 text-center">
            <Dumbbell className="w-20 h-20 text-ink-300 mx-auto mb-6" />
            <h1 className="font-display text-4xl text-ink-900 tracking-wider mb-4">PRODUCT NOT FOUND</h1>
            <p className="text-ink-500 mb-8">The product you're looking for doesn't exist.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-athletic-500 text-white font-black uppercase tracking-wider text-sm hover:bg-athletic-600 transition-colors">
              Back to Shop
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleQuantityChange = (delta) => setQuantity(prev => Math.max(1, prev + delta));
  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Sport Banner */}
        <section className="relative h-48 overflow-hidden bg-ink-950">
          <img src={siteImages.productBanner || siteImages.heroMain} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/70 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl w-full mx-auto px-4 lg:px-8">
              <nav className="flex items-center gap-2 text-white/70 text-xs font-bold uppercase tracking-wider">
                <Link to="/" className="hover:text-athletic-400 transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <Link to="/shop" className="hover:text-athletic-400 transition-colors">Shop</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <Link to={`/shop?category=${category?.slug}`} className="hover:text-athletic-400 transition-colors">{category?.name}</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-white truncate max-w-[180px]">{product.name}</span>
              </nav>
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mt-4">{category?.name}</p>
            </div>
          </div>
        </section>

        {/* Product details */}
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <Link to="/shop" className="inline-flex items-center gap-2 text-ink-500 hover:text-athletic-500 text-sm font-bold uppercase tracking-wider mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Shop
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Image */}
            <div className="relative">
              <div className="aspect-square bg-ink-50 overflow-hidden border-2 border-ink-100">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              </div>
              {product.badge && (
                <div className="absolute top-5 left-0">
                  <span className="inline-block bg-athletic-500 text-white text-[11px] font-black tracking-[0.25em] uppercase px-5 py-2 relative shadow-lg">
                    {product.badge}
                    <span className="absolute top-0 right-[-10px] h-full w-0 border-t-[34px] border-t-athletic-500 border-r-[10px] border-r-transparent" />
                  </span>
                </div>
              )}
              <div className="absolute bottom-5 right-5 bg-volt-400 text-ink-900 px-4 py-2 font-black text-xs uppercase tracking-wider">
                SKU: {product.sku}
              </div>
            </div>

            {/* Info */}
            <div>
              <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-3">{category?.name}</p>
              <h1 className="font-display text-4xl lg:text-5xl text-ink-900 tracking-wider leading-none mb-5">
                {product.name.toUpperCase()}
              </h1>
              <p className="text-ink-600 text-lg leading-relaxed mb-6">{product.description}</p>

              <div className="flex items-end gap-5 pb-6 border-b-2 border-ink-100">
                <span className="font-display text-5xl text-ink-900 leading-none">${product.price.toFixed(2)}</span>
                <span className="px-3 py-1.5 bg-athletic-50 text-athletic-600 text-[11px] font-black uppercase tracking-wider border border-athletic-100">In Stock • Ships Fast</span>
              </div>

              {product.keyBenefits && (
                <div className="py-6 border-b-2 border-ink-100">
                  <h3 className="font-display text-xl text-ink-900 tracking-wider mb-4">KEY BENEFITS</h3>
                  <ul className="space-y-2.5">
                    {product.keyBenefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-athletic-500 mt-0.5 flex-shrink-0" />
                        <span className="text-ink-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {product.usage && (
                <div className="py-6 border-b-2 border-ink-100">
                  <h3 className="font-display text-xl text-ink-900 tracking-wider mb-3">HOW TO USE</h3>
                  <p className="text-ink-600 leading-relaxed">{product.usage}</p>
                </div>
              )}

              {product.features && (
                <div className="py-6 border-b-2 border-ink-100">
                  <h3 className="font-display text-xl text-ink-900 tracking-wider mb-4">FEATURES</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature, i) => (
                      <span key={i} className="px-3 py-1.5 bg-ink-50 text-ink-700 text-xs font-bold uppercase tracking-wider border border-ink-200">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Qty + Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-3 py-6">
                <div className="flex items-center border-2 border-ink-200">
                  <button onClick={() => handleQuantityChange(-1)} className="p-4 text-ink-700 hover:bg-ink-50">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-14 text-center font-black text-lg text-ink-900">{quantity}</span>
                  <button onClick={() => handleQuantityChange(1)} className="p-4 text-ink-700 hover:bg-ink-50">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 flex items-center justify-center gap-3 px-8 py-4 font-black uppercase tracking-wider transition-all ${
                    addedToCart
                      ? 'bg-green-500 text-white'
                      : 'bg-athletic-500 hover:bg-athletic-600 text-white shadow-button hover:shadow-card-hover'
                  }`}
                >
                  {addedToCart ? (
                    <><CheckCircle2 className="w-5 h-5" /> Added!</>
                  ) : (
                    <><ShoppingBag className="w-5 h-5" /> Add to Cart — ${(product.price * quantity).toFixed(2)}</>
                  )}
                </button>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-3 pt-4">
                {[
                  { icon: Truck, title: 'Fast USA Shipping', sub: 'Free over $75' },
                  { icon: Shield, title: 'Secure Checkout', sub: 'SSL Protected' },
                  { icon: RotateCcw, title: '30-Day Returns', sub: 'No hassle' },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-4 bg-ink-50 border border-ink-100">
                    <item.icon className="w-5 h-5 text-athletic-500 mb-2" />
                    <span className="text-ink-900 text-xs font-black uppercase tracking-wider">{item.title}</span>
                    <span className="text-ink-500 text-[11px] mt-0.5">{item.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Related */}
          {relatedProducts.length > 0 && (
            <div className="mt-24">
              <div className="flex items-end justify-between mb-8">
                <div>
                  <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-2">You May Also Like</p>
                  <h2 className="font-display text-4xl lg:text-5xl text-ink-900 tracking-wider">GEAR UP MORE</h2>
                </div>
                <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-ink-900 font-bold uppercase tracking-wider text-sm hover:text-athletic-500 transition-colors">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedProducts.map(rp => (
                  <ProductCard key={rp.id} product={rp} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
